import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem
} from '@mui/material';
import {
    Person as PersonIcon,
    PersonAdd as PersonAddIcon,
    VerifiedUser as VerifiedUserIcon,
    PersonOff as PersonOffIcon
} from '@mui/icons-material';

const UserRegistrationAnalytics = ({ onError, onLoading }) => {
    const [analytics, setAnalytics] = useState({
        totalCounts: [],
        dailyCounts: [],
        recentRegistrations: []
    });
    const [, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userTypeFilter, setUserTypeFilter] = useState('all');

    const fetchRegistrationAnalytics = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const params = new URLSearchParams();
            if (userTypeFilter !== 'all') {
                params.append('user_type', userTypeFilter);
            }

            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/user-auth/registration-analytics?${params}`);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.success) {
                setAnalytics(data.analytics);
            } else {
                const errorMsg = data.error || 'Failed to fetch registration analytics';
                setError(errorMsg);
                if (onError) onError(errorMsg);
            }
        } catch (err) {
            console.error('Error fetching registration analytics:', err);
            const errorMsg = 'Failed to fetch registration analytics';
            setError(errorMsg);
            if (onError) onError(errorMsg);
        } finally {
            setLoading(false);
            if (onLoading) onLoading(false);
        }
    }, [userTypeFilter, onError, onLoading]); // Dependencies for useCallback


    useEffect(() => {
        fetchRegistrationAnalytics();
    }, [fetchRegistrationAnalytics]); // Include fetchRegistrationAnalytics as dependency


    const getUserTypeIcon = (userType) => {
        switch (userType) {
            case 'registered':
                return <VerifiedUserIcon />;
            case 'guest':
                return <PersonIcon />;
            default:
                return <PersonOffIcon />;
        }
    };

    const getUserTypeColor = (userType) => {
        switch (userType) {
            case 'registered':
                return 'success';
            case 'guest':
                return 'primary';
            default:
                return 'default';
        }
    };

    const getTotalStats = () => {
        const totalUsers = analytics.totalCounts?.reduce((sum, item) => sum + item.count, 0) || 0;
        const verifiedUsers = analytics.totalCounts?.reduce((sum, item) => sum + (item.verified_count || 0), 0) || 0;
        const unverifiedUsers = analytics.totalCounts?.reduce((sum, item) => sum + (item.unverified_count || 0), 0) || 0;

        return { totalUsers, verifiedUsers, unverifiedUsers };
    };

    const stats = getTotalStats();

    return (
        <Box>
            <Typography variant="h5" gutterBottom>
                User Registration Analytics
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Filter Controls */}
            <Box sx={{ mb: 3 }}>
                <FormControl sx={{ minWidth: 200 }}>
                    <InputLabel>User Type</InputLabel>
                    <Select
                        value={userTypeFilter}
                        label="User Type"
                        onChange={(e) => setUserTypeFilter(e.target.value)}
                    >
                        <MenuItem value="all">All Users</MenuItem>
                        <MenuItem value="registered">Registered Users</MenuItem>
                        <MenuItem value="guest">Guest Users</MenuItem>
                    </Select>
                </FormControl>
            </Box>

            {/* Summary Cards */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Total Users
                                    </Typography>
                                    <Typography variant="h4" color="primary.main">
                                        {stats.totalUsers}
                                    </Typography>
                                </Box>
                                <PersonAddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Verified Users
                                    </Typography>
                                    <Typography variant="h4" color="success.main">
                                        {stats.verifiedUsers}
                                    </Typography>
                                </Box>
                                <VerifiedUserIcon sx={{ fontSize: 40, color: 'success.main' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Box>
                                    <Typography color="textSecondary" gutterBottom>
                                        Unverified Users
                                    </Typography>
                                    <Typography variant="h4" color="warning.main">
                                        {stats.unverifiedUsers}
                                    </Typography>
                                </Box>
                                <PersonOffIcon sx={{ fontSize: 40, color: 'warning.main' }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <Card>
                        <CardContent>
                            <Typography color="textSecondary" gutterBottom>
                                Verification Rate
                            </Typography>
                            <Typography variant="h4" color="info.main">
                                {stats.totalUsers > 0 ? Math.round((stats.verifiedUsers / stats.totalUsers) * 100) : 0}%
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* User Type Breakdown */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        User Type Breakdown
                    </Typography>
                    <Grid container spacing={2}>
                        {analytics.totalCounts?.map((item) => (
                            <Grid item xs={12} sm={6} md={4} key={item.user_type}>
                                <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                                    <Box display="flex" alignItems="center" mb={1}>
                                        {getUserTypeIcon(item.user_type)}
                                        <Typography variant="h6" sx={{ ml: 1 }}>
                                            {item.user_type.charAt(0).toUpperCase() + item.user_type.slice(1)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="h4" color={`${getUserTypeColor(item.user_type)}.main`}>
                                        {item.count}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                        Verified: {item.verified_count || 0} | Unverified: {item.unverified_count || 0}
                                    </Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* Recent Registrations Table */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Recent Registrations
                    </Typography>
                    {analytics.recentRegistrations?.length > 0 ? (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>User</TableCell>
                                        <TableCell>Type</TableCell>
                                        <TableCell>Email</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell>Games Played</TableCell>
                                        <TableCell>Total Score</TableCell>
                                        <TableCell>Best Score</TableCell>
                                        <TableCell>Registered</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {analytics.recentRegistrations.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight="medium">
                                                    {user.fname} {user.lname || ''}
                                                </Typography>
                                                {user.guest_name && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        ({user.guest_name})
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.user_type}
                                                    color={getUserTypeColor(user.user_type)}
                                                    size="small"
                                                    icon={getUserTypeIcon(user.user_type)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                {user.email || '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={user.email_verified ? 'Verified' : 'Unverified'}
                                                    color={user.email_verified ? 'success' : 'warning'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>{user.total_games_played || 0}</TableCell>
                                            <TableCell>{user.total_score || 0}</TableCell>
                                            <TableCell>{user.best_score || 0}</TableCell>
                                            <TableCell>
                                                {new Date(user.created_at).toLocaleString()}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Alert severity="info">
                            No recent registrations found
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default UserRegistrationAnalytics;
